import React, { useContext, useLayoutEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import { getEditorListCrumb } from './editorListCrumb';
import { AppBarPageChromeContext } from 'components/adminLayout/AppBarPageChromeContext';

const DEFAULT_PARENT = { listPath: '/app/courses', listLabel: 'Home' };

export type EditorBreadcrumbsProps = {
  /** Current screen title (e.g. item name). */
  currentLabel: string;
  /** Optional parent breadcrumb trail; when omitted, falls back to route-based inference. */
  parentItems?: Array<{
    label: string;
    to?: string;
  }>;
};

function BreadcrumbsContent({ currentLabel, parentItems }: EditorBreadcrumbsProps) {
  const { pathname } = useLocation();
  const listCrumb = getEditorListCrumb(pathname);
  const parents = parentItems && parentItems.length > 0
    ? parentItems
    : [{ label: (listCrumb ?? DEFAULT_PARENT).listLabel, to: (listCrumb ?? DEFAULT_PARENT).listPath }];

  return (
    <Breadcrumbs
      separator="›"
      aria-label="breadcrumb"
      sx={{
        pt: 0,
        pb: 0,
        flexShrink: 0,
        '& .MuiBreadcrumbs-separator': { mx: 0.75 },
      }}
    >
      {parents.map((parent) => (
        parent.to ? (
          <Link
            key={`${parent.label}-${parent.to}`}
            component={RouterLink}
            to={parent.to}
            underline="hover"
            color="inherit"
            variant="body2"
          >
            {parent.label}
          </Link>
        ) : (
          <Typography
            key={parent.label}
            color="text.primary"
            variant="body2"
            fontWeight={500}
          >
            {parent.label}
          </Typography>
        )
      ))}
      <Typography color="text.primary" variant="body2" fontWeight={500}>
        {currentLabel}
      </Typography>
    </Breadcrumbs>
  );
}

/**
 * List → current title. When {@link AppBarPageChromeProvider} is present, renders in the app bar above the page title.
 * Otherwise renders inline (e.g. Storybook).
 */
const EditorBreadcrumbs = ({ currentLabel, parentItems }: EditorBreadcrumbsProps) => {
  const chrome = useContext(AppBarPageChromeContext);
  const { pathname } = useLocation();

  const tree = useMemo(
    () => <BreadcrumbsContent currentLabel={currentLabel} parentItems={parentItems} />,
    [currentLabel, parentItems, pathname],
  );

  useLayoutEffect(() => {
    if (!chrome) {
      return undefined;
    }
    chrome.setAppBarBreadcrumb(tree);
    return () => {
      chrome.setAppBarBreadcrumb(null);
    };
  }, [chrome, tree]);

  if (chrome) {
    return null;
  }

  return (
    <Box
      sx={{
        px: 2,
        pt: 2,
        pb: 1,
      }}
    >
      <BreadcrumbsContent currentLabel={currentLabel} parentItems={parentItems} />
    </Box>
  );
};

export default EditorBreadcrumbs;
